import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Calendar, MapPin, Cloud, ClipboardList, Rocket, Info, Pencil } from 'lucide-react';
import { subtypeColor, cmStatusColor, launcherStatusClasses, launcherStatusLabel } from './subtype-colors';
import { formatEventDate } from '../utils/dates';

interface PageHeaderProps {
  campaignId: string;
  campaignName: string;
  subtype: string | null;
  status: string;
  launcherStatus: string;
  eventDate: string | null;
  city: string | null;
  currentTab: 'manager' | 'launcher';
  /** If provided, intercepts the back link click (e.g. for unsaved-changes guard) */
  onBackClick?: () => void;
  /** Toggle event details panel */
  detailsOpen?: boolean;
  onToggleDetails?: () => void;
  /** Open the edit sidebar */
  onEditDetails?: () => void;
}

function SubtypeInitials({ subtype }: { subtype: string | null }) {
  const colors = subtypeColor(subtype);
  const initials = subtype
    ? subtype.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase()
    : '?';
  return (
    <div className={`w-9 h-9 rounded-lg ${colors.bg} ${colors.fg} flex items-center justify-center text-xs font-bold flex-shrink-0`}>
      {initials}
    </div>
  );
}

function StatusPill({ status }: { status: string }) {
  const colors = cmStatusColor(status);
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${colors.bg} ${colors.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${colors.text.replace('text-', 'bg-')}`} />
      {status}
    </span>
  );
}

function SubtypeChip({ subtype }: { subtype: string | null }) {
  if (!subtype) return null;
  const colors = subtypeColor(subtype);
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${colors.fg}`}>
      <span className={`w-2 h-2 rounded-full ${colors.dot}`} />
      {subtype}
    </span>
  );
}

export function PageHeader({
  campaignId,
  campaignName,
  subtype,
  status,
  launcherStatus,
  eventDate,
  city,
  currentTab,
  onBackClick,
  detailsOpen,
  onToggleDetails,
  onEditDetails,
}: PageHeaderProps) {
  const navigate = useNavigate();

  const formattedDate = formatEventDate(eventDate);

  return (
    <div className="mb-6">
      {/* Nav row: Back + tabs inline */}
      <div className="flex items-center gap-1 border-b border-nightshade-200 dark:border-nightshade-700 mb-4">
        {onBackClick ? (
          <button
            onClick={onBackClick}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm text-nightshade-500 dark:text-nightshade-400 hover:text-blueberry-600 dark:hover:text-blueberry-400 transition-colors border-b-2 border-transparent -mb-px"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            All Events
          </button>
        ) : (
          <Link
            to="/launcher"
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm text-nightshade-500 dark:text-nightshade-400 hover:text-blueberry-600 dark:hover:text-blueberry-400 transition-colors border-b-2 border-transparent -mb-px"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            All Events
          </Link>
        )}
        <div className="w-px h-5 bg-nightshade-200 dark:bg-nightshade-700 mx-1" />
        <button
          onClick={() => navigate(`/launcher/${campaignId}/manager`)}
          className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${
            currentTab === 'manager'
              ? 'border-blueberry-500 text-blueberry-600 dark:text-blueberry-400'
              : 'border-transparent text-nightshade-500 dark:text-nightshade-400 hover:text-nightshade-700 dark:hover:text-nightshade-300'
          }`}
        >
          <ClipboardList className="w-3.5 h-3.5" />
          Manager
        </button>
        <button
          onClick={() => navigate(`/launcher/${campaignId}`)}
          className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${
            currentTab === 'launcher'
              ? 'border-blueberry-500 text-blueberry-600 dark:text-blueberry-400'
              : 'border-transparent text-nightshade-500 dark:text-nightshade-400 hover:text-nightshade-700 dark:hover:text-nightshade-300'
          }`}
        >
          <Rocket className="w-3.5 h-3.5" />
          Launcher
        </button>
        {(onToggleDetails || onEditDetails) && (
          <>
            <div className="flex-1" />
            {onToggleDetails && (
              <button
                onClick={onToggleDetails}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border border-nightshade-200 dark:border-nightshade-600 text-nightshade-600 dark:text-nightshade-300 hover:bg-nightshade-100 dark:hover:bg-nightshade-800 transition-colors -mb-px"
              >
                <Info className="w-3.5 h-3.5" />
                {detailsOpen ? 'Hide Campaign Details' : 'Campaign Details'}
              </button>
            )}
            {onEditDetails && (
              <button
                onClick={onEditDetails}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border border-nightshade-200 dark:border-nightshade-600 text-nightshade-600 dark:text-nightshade-300 hover:bg-nightshade-100 dark:hover:bg-nightshade-800 transition-colors -mb-px"
              >
                <Pencil className="w-3.5 h-3.5" />
                Edit Campaign Details
              </button>
            )}
          </>
        )}
      </div>

      {/* Title row */}
      <div className="flex items-start gap-3 mb-2">
        <SubtypeInitials subtype={subtype} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-xl font-semibold tracking-tight text-nightshade-950 dark:text-nightshade-50 truncate">
              {campaignName}
            </h1>
            <a
              href={`https://clay.lightning.force.com/lightning/r/Campaign/${campaignId}/view`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 px-2 py-0.5 rounded text-xs text-nightshade-400 hover:text-blueberry-500 hover:bg-nightshade-100 dark:hover:bg-nightshade-800 transition-colors flex-shrink-0"
              title="View in Salesforce"
            >
              <Cloud className="w-3.5 h-3.5 text-blueberry-600 dark:text-blueberry-400" />
              <span className="hidden sm:inline">SFDC</span>
            </a>
          </div>

          {/* Meta line */}
          <div className="flex items-center gap-2 mt-1 flex-wrap text-sm text-nightshade-500 dark:text-nightshade-400">
            <StatusPill status={status} />
            <span className="text-nightshade-300 dark:text-nightshade-600">·</span>
            <SubtypeChip subtype={subtype} />
            {formattedDate && (
              <>
                <span className="text-nightshade-300 dark:text-nightshade-600">·</span>
                <span className="inline-flex items-center gap-1">
                  <Calendar className="w-3.5 h-3.5" />
                  {formattedDate}
                </span>
              </>
            )}
            {city && (
              <>
                <span className="text-nightshade-300 dark:text-nightshade-600">·</span>
                <span className="inline-flex items-center gap-1">
                  <MapPin className="w-3.5 h-3.5" />
                  {city}
                </span>
              </>
            )}
            <span className="text-nightshade-300 dark:text-nightshade-600">·</span>
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${launcherStatusClasses(launcherStatus)}`}>
              {launcherStatusLabel(launcherStatus)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
